import { useReducer, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";

export type FilterValue = string | string[] | undefined;
export type Filters = Record<string, FilterValue>;

type FilterAction =
  | { type: "SET_FILTER"; key: string; value: FilterValue }
  | { type: "CLEAR_FILTER"; key: string }
  | { type: "CLEAR_ALL" }
  | { type: "SYNC_FROM_URL"; filters: Filters };

interface UseListFiltersOptions {
  filterKeys: string[];
  multiSelectKeys?: string[];
}

function filtersReducer(state: Filters, action: FilterAction): Filters {
  switch (action.type) {
    case "SET_FILTER":
      return {
        ...state,
        [action.key]: action.value,
      };
    case "CLEAR_FILTER":
      return {
        ...state,
        [action.key]: undefined,
      };
    case "CLEAR_ALL":
      return Object.keys(state).reduce((acc, key) => {
        acc[key] = undefined;
        return acc;
      }, {} as Filters);
    case "SYNC_FROM_URL":
      return action.filters;
    default:
      return state;
  }
}

export const useListFilters = (options: UseListFiltersOptions) => {
  const { filterKeys, multiSelectKeys = [] } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize filters from URL
  const getFiltersFromURL = useCallback((): Filters => {
    const filters: Filters = {};
    filterKeys.forEach((key) => {
      const value = searchParams.get(key);
      if (value) {
        // If it's a multi-select key, split by comma
        if (multiSelectKeys.includes(key)) {
          filters[key] = value.split(",").filter(Boolean);
        } else {
          filters[key] = value;
        }
      } else {
        filters[key] = undefined;
      }
    });
    return filters;
  }, [filterKeys, multiSelectKeys, searchParams]);

  const [filters, dispatch] = useReducer(
    filtersReducer,
    null,
    getFiltersFromURL
  );

  // Sync URL with filter state
  const syncToURL = useCallback(
    (newFilters: Filters) => {
      setSearchParams((prev) => {
        // Reset page when filters change
        prev.set("page", "1");

        // Update all filter keys
        filterKeys.forEach((key) => {
          const value = newFilters[key];
          if (value) {
            // Handle array values (multi-select)
            const stringValue = Array.isArray(value) ? value.join(",") : value;
            prev.set(key, stringValue);
          } else {
            prev.delete(key);
          }
        });

        return prev;
      });
    },
    [filterKeys, setSearchParams]
  );

  // Handle filter change
  const setFilter = useCallback(
    (key: string, value: FilterValue) => {
      const isMultiSelect = multiSelectKeys.includes(key);

      let newValue: FilterValue = value;

      if (isMultiSelect && typeof value === "string") {
        // For multi-select, toggle the value in the array
        const currentValues = (filters[key] as string[]) || [];
        if (currentValues.includes(value)) {
          // Remove value
          newValue = currentValues.filter((v) => v !== value);
          if (newValue.length === 0) newValue = undefined;
        } else {
          // Add value
          newValue = [...currentValues, value];
        }
      }

      const newFilters = {
        ...filters,
        [key]: newValue,
      };

      dispatch({ type: "SET_FILTER", key, value: newValue });
      syncToURL(newFilters);
    },
    [filters, multiSelectKeys, syncToURL]
  );

  // Handle clear filter
  const clearFilter = useCallback(
    (key: string) => {
      const newFilters = {
        ...filters,
        [key]: undefined,
      };

      dispatch({ type: "CLEAR_FILTER", key });
      syncToURL(newFilters);
    },
    [filters, syncToURL]
  );

  // Handle clear all filters
  const clearAllFilters = useCallback(() => {
    const clearedFilters = Object.keys(filters).reduce((acc, key) => {
      acc[key] = undefined;
      return acc;
    }, {} as Filters);

    dispatch({ type: "CLEAR_ALL" });
    syncToURL(clearedFilters);
  }, [filters, syncToURL]);

  // Check if a specific value is selected for a filter
  const isFilterSelected = useCallback(
    (key: string, value: string): boolean => {
      const filterValue = filters[key];
      if (Array.isArray(filterValue)) {
        return filterValue.includes(value);
      }
      return filterValue === value;
    },
    [filters]
  );

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return filterKeys.some((key) => {
      const value = filters[key];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== undefined && value !== "";
    });
  }, [filters, filterKeys]);

  return {
    filters,
    setFilter,
    clearFilter,
    clearAllFilters,
    isFilterSelected,
    hasActiveFilters,
  };
};
